/**
 * Utility functions for image processing
 */

/**
 * Resizes an image to the target dimensions while maintaining aspect ratio
 * @param {string} imageUrl - URL of the image to resize
 * @param {Array} targetSize - Target dimensions [width, height]
 * @returns {Promise<Blob>} - A promise that resolves to the resized image as a Blob
 */
export const resizeImage = async (imageUrl, targetSize = [800, 450]) => {
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.crossOrigin = "anonymous";
        
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Calculate new dimensions maintaining aspect ratio
            let width = img.width;
            let height = img.height;
            let ratio = targetSize[0] / width;
            
            if (height * ratio > targetSize[1]) {
                ratio = targetSize[1] / height;
            }
            
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
            
            // Set canvas size
            canvas.width = width;
            canvas.height = height;
            
            // Draw image with white background
            ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
            
            // Convert to blob
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/jpeg', 0.9);
        };
        
        img.onerror = (error) => {
            reject(new Error('Failed to load image: ' + error));
        };

        img.src = imageUrl;
    });
};

/**
 * Crops an image to the target dimensions
 * @param {string} imageUrl - URL of the image to crop
 * @param {Object} cropArea - {x, y, width, height} representing the crop area
 * @returns {Promise<Blob>} - A promise that resolves to the cropped image as a Blob
 */
export const cropImage = async (imageUrl, cropArea) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Set canvas size to crop area
            canvas.width = cropArea.width;
            canvas.height = cropArea.height;
            
            // Draw cropped region
            ctx.drawImage(
                img,
                cropArea.x, cropArea.y, cropArea.width, cropArea.height, // Source coordinates
                0, 0, cropArea.width, cropArea.height                   // Destination coordinates
            );
            
            // Convert to blob
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/jpeg', 0.9);
        };
        
        img.onerror = (error) => {
            reject(new Error('Failed to load image: ' + error));
        };
        
        img.src = imageUrl;
    });
};

/**
 * Creates a thumbnail from an image
 * @param {string} imageUrl - URL of the image
 * @param {Array} size - Thumbnail size [width, height]
 * @returns {Promise<Blob>} - A promise that resolves to the thumbnail as a Blob
 */
export const createThumbnail = async (imageUrl, size = [240, 135]) => {
    return resizeImage(imageUrl, size);
}; 